import { useState } from "react";
import { toast } from "sonner";
import { Link2, Trash2, UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAdminGet } from "@/pages/admin/SuperAdmin";
import { Badge, EntityForm } from "@/pages/admin/regie/ui";

const TYPES = [["COMMUNITY", "Community"], ["BUSINESS", "Business"], ["PARTNER", "Partner"], ["EXPERIENCE_PROVIDER", "Experience"]];
const CAPS = [["ORGANIZATION_MANAGER", "Gestion de l'organisation"], ["EDITOR", "Édition du contenu"], ["COMMERCIAL", "Commercial"], ["FINANCE", "Finance"], ["PARTNER", "Partenaire"], ["EXPERIENCE_MANAGER", "Expériences"]];
const LTYPES = [["PRIVATE", "Entre amis"], ["COMMUNITY", "Community"], ["BUSINESS", "Business"], ["PARTNER", "Partner"], ["EXPERIENCE", "Experience"]];
const NEED = { COMMUNITY: "COMMUNITY", BUSINESS: "BUSINESS", PARTNER: "PARTNER", EXPERIENCE: "EXPERIENCE_PROVIDER" };
const ltLabel = (t) => (LTYPES.find(([k]) => k === t) || [t, t])[1];
const FIELDS = [
  { k: "name", label: "Nom", required: true }, { k: "types", label: "Types", type: "multi", options: TYPES },
  { k: "country", label: "Pays", type: "select", options: [["CA", "Canada"], ["FR", "France"]], default: "CA" },
  { k: "region", label: "Région / province" }, { k: "city", label: "Ville" },
  { k: "timezone", label: "Fuseau", type: "select", options: [["America/Toronto", "America/Toronto"], ["Europe/Paris", "Europe/Paris"]], default: "America/Toronto" },
  { k: "currency", label: "Devise", type: "select", options: [["CAD", "CAD"], ["EUR", "EUR"], ["USD", "USD"]], default: "CAD" },
  { k: "language", label: "Langue", type: "select", options: [["fr", "Français"], ["en", "Anglais"]], default: "fr" },
  { k: "legal_name", label: "Raison sociale" }, { k: "website", label: "Site web (https://)" },
  { k: "description", label: "Description", type: "textarea" }, { k: "notes", label: "Notes internes", type: "textarea" },
];
const EMPTY = { name: "", types: ["COMMUNITY"], country: "CA", timezone: "America/Toronto", currency: "CAD", language: "fr" };
const KEEP = ["name", "types", "legal_name", "description", "country", "region", "city", "language", "timezone", "currency", "website", "social_links", "branding", "status", "notes", "partner_id"];
const typeLabel = (t) => (TYPES.find(([k]) => k === t) || [t, t])[1];

function Members({ o, load }) {
  const [email, setEmail] = useState("");
  const [caps, setCaps] = useState(["ORGANIZATION_MANAGER"]);
  const add = async () => {
    try { await api.post(`/superadmin/organizations/${o.id}/members`, { email, capabilities: caps }); toast.success("Accès enregistré"); setEmail(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const drop = async (uid) => { try { await api.delete(`/superadmin/organizations/${o.id}/members/${uid}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-2" data-testid={`org-members-${o.id}`}>
      <p className="text-xs font-semibold text-slate-300">Personnes et capacités</p>
      {o.members.map((m) => (
        <p key={m.user_id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`org-member-${m.user_id}`}>
          <span className="font-semibold">{m.display_name || m.email}</span><span className="text-slate-500">{m.email}</span>
          {m.capabilities.map((c) => <Badge key={c}>{(CAPS.find(([k]) => k === c) || [c, c])[1]}</Badge>)}
          <button onClick={() => drop(m.user_id)} className="text-slate-400 hover:text-red-300" aria-label="Retirer" data-testid={`org-member-remove-${m.user_id}`}><Trash2 size={12} /></button>
        </p>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="courriel d'un compte existant" className="mpl-input w-60 py-1.5 text-xs" data-testid={`org-member-email-${o.id}`} />
        {CAPS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-1 text-[11px] text-slate-300"><input type="checkbox" checked={caps.includes(k)} onChange={(e) => setCaps(e.target.checked ? [...caps, k] : caps.filter((x) => x !== k))} className="accent-[#B7F52A]" data-testid={`org-cap-${o.id}-${k}`} /> {l}</label>
        ))}
        <button onClick={add} disabled={!email || !caps.length} className="mpl-btn-ghost px-3 py-1.5 text-xs" data-testid={`org-member-add-${o.id}`}><UserPlus size={12} /> Donner l'accès</button>
      </div>
    </div>
  );
}

function AttachLeague({ o, load }) {
  const valid = LTYPES.filter(([k]) => NEED[k] && o.types.includes(NEED[k]));
  const [lid, setLid] = useState("");
  const [lt, setLt] = useState(valid[0]?.[0] || "COMMUNITY");
  const set = async (leagueId, type, orgId) => {
    try { await api.put(`/superadmin/leagues/${leagueId}/classification`, { league_type: type, organization_id: orgId }); toast.success("Ligue mise à jour"); setLid(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid={`org-leagues-${o.id}`}>
      <p className="text-xs font-semibold text-slate-300">Ligues rattachées</p>
      {o.leagues.map((l) => (
        <p key={l.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`org-league-${l.id}`}>
          <span className="font-semibold">{l.name}</span><Badge>{ltLabel(l.league_type)}</Badge><span className="text-slate-500">{l.member_count} membres</span>
          {l.league_type !== "BUSINESS" && <button onClick={() => set(l.id, "PRIVATE", null)} className="text-slate-400 underline" data-testid={`org-league-detach-${l.id}`}>Détacher</button>}
        </p>
      ))}
      {!o.leagues.length && <p className="text-xs text-slate-500">Aucune ligue.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <input value={lid} onChange={(e) => setLid(e.target.value.trim())} placeholder="Identifiant de la ligue (onglet Ligues)" className="mpl-input w-72 py-1.5 text-xs" data-testid={`org-league-id-${o.id}`} />
        <select value={lt} onChange={(e) => setLt(e.target.value)} className="mpl-input w-auto py-1.5 text-xs" data-testid={`org-league-type-${o.id}`}>
          {valid.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button onClick={() => set(lid, lt, o.id)} disabled={!lid} className="mpl-btn-ghost px-3 py-1.5 text-xs" data-testid={`org-league-attach-${o.id}`}><Link2 size={12} /> Rattacher</button>
      </div>
      <p className="text-[11px] text-slate-500">Une ligue Business doit déjà être une ligue d'entreprise (créée dans « Ligues d'entreprise ») ; tu peux ici la rattacher à une organisation Business.</p>
    </div>
  );
}

function OrgDetail({ id, onExpired, onChange }) {
  const { data: o, load: reload } = useAdminGet(`/superadmin/organizations/${id}`, onExpired);
  const load = () => { reload(); onChange(); };
  if (!o) return null;
  return <div className="mt-3 space-y-4 border-t border-white/10 pt-3"><AttachLeague o={o} load={load} /><Members o={o} load={load} /></div>;
}

export default function OrganizationsAdmin({ onExpired }) {
  const { data, load } = useAdminGet("/superadmin/organizations", onExpired);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const [market, setMarket] = useState("");
  const save = async (body) => {
    const b = Object.fromEntries(KEEP.filter((k) => body[k] !== undefined).map((k) => [k, body[k] === "" ? null : body[k]]));
    try { if (edit.id) await api.put(`/superadmin/organizations/${edit.id}`, b); else await api.post("/superadmin/organizations", b); toast.success("Organisation enregistrée"); setEdit(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const archive = async (id) => { try { await api.delete(`/superadmin/organizations/${id}`); toast.success("Archivée"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const rows = (data || []).filter((o) => !market || o.country === market);
  return (
    <section className="space-y-3" data-testid="orgs-admin">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-lg font-bold">Organisations <span className="text-slate-500">({rows.length})</span></h2>
        <select value={market} onChange={(e) => setMarket(e.target.value)} className="mpl-input w-auto py-1.5 text-xs" data-testid="orgs-market">
          <option value="">Tous marchés</option><option value="FR">France</option><option value="CA">Canada</option>
        </select>
        <button onClick={() => setEdit({ ...EMPTY })} className="mpl-btn-primary text-xs" data-testid="orgs-new">Nouvelle organisation</button>
      </div>
      <p className="text-xs text-slate-400">Une organisation peut cumuler plusieurs types (par exemple Community et Partner). Les ligues gardent le moteur de jeu commun.</p>
      {edit && <EntityForm key={edit.id || "new"} fields={FIELDS} initial={edit} onSubmit={save} onCancel={() => setEdit(null)} testId="orgs-form" />}
      <ul className="space-y-2">
        {rows.map((o) => (
          <li key={o.id} className="mpl-card p-3 text-xs" data-testid={`org-row-${o.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <p className="mr-auto min-w-0"><b className="text-sm">{o.name}</b> <span className="text-slate-500">/{o.slug} · {o.country === "FR" ? "France" : "Canada"}{o.city ? ` · ${o.city}` : ""} · {o.currency} · {o.league_count} ligue(s)</span></p>
              {o.types.map((t) => <Badge key={t}>{typeLabel(t)}</Badge>)}
              <button onClick={() => setOpen(open === o.id ? null : o.id)} className="mpl-btn-ghost px-2 py-1 text-xs" data-testid={`org-open-${o.id}`}>Ligues et accès</button>
              <button onClick={() => setEdit(o)} className="mpl-btn-ghost px-2 py-1 text-xs" data-testid={`org-edit-${o.id}`}>Modifier</button>
              <button onClick={() => window.confirm("Archiver cette organisation ?") && archive(o.id)} className="mpl-btn-ghost px-2 py-1 text-xs" data-testid={`org-archive-${o.id}`}>Archiver</button>
            </div>
            {open === o.id && <OrgDetail id={o.id} onExpired={onExpired} onChange={load} />}
          </li>
        ))}
        {data && !rows.length && <li className="text-xs text-slate-500" data-testid="orgs-empty">Aucune organisation pour l'instant.</li>}
      </ul>
    </section>
  );
}
